import { useEffect } from 'react';
import { create } from 'zustand';

import { Icon } from './icons.js';
import styles from './toast.module.css';

export type ToastTone = 'exito' | 'error' | 'info';

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly title: string;
  readonly message?: string;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  readonly push: (toast: Omit<Toast, 'id'>) => void;
  readonly dismiss: (id: number) => void;
}

let nextId = 0;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(toast): void {
    nextId += 1;
    const id = nextId;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
  },
  dismiss(id): void {
    set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) }));
  },
}));

/** Se llama desde fuera de React (casos de uso, mutaciones). */
export const toast = {
  exito: (title: string, message?: string) =>
    { useToastStore.getState().push({ tone: 'exito', title, ...(message === undefined ? {} : { message }) }); },
  error: (title: string, message?: string) =>
    { useToastStore.getState().push({ tone: 'error', title, ...(message === undefined ? {} : { message }) }); },
  info: (title: string, message?: string) =>
    { useToastStore.getState().push({ tone: 'info', title, ...(message === undefined ? {} : { message }) }); },
};

const ICONOS: Readonly<Record<ToastTone, 'tendencia' | 'alertas' | 'inicio'>> = {
  exito: 'tendencia',
  error: 'alertas',
  info: 'inicio',
};

/**
 * Región de avisos.
 *
 * `role="status"` con `aria-live="polite"` los anuncia a lectores de pantalla
 * sin interrumpir lo que el usuario esté haciendo.
 */
export function ToastRegion() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div aria-live="polite" className={styles.region} role="status">
      {toasts.map((item) => (
        <ToastItem key={item.id} onDismiss={dismiss} toast={item} />
      ))}
    </div>
  );
}

function ToastItem({
  toast: item,
  onDismiss,
}: Readonly<{ readonly toast: Toast; readonly onDismiss: (id: number) => void }>) {
  // Los errores no se van solos: el usuario necesita poder leerlos con calma.
  const duracion = item.tone === 'error' ? 8000 : 4000;

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(item.id);
    }, duracion);
    return () => {
      clearTimeout(timer);
    };
  }, [item.id, duracion, onDismiss]);

  return (
    <div className={`${styles.toast} ${styles[item.tone]}`}>
      <span className={styles.icon}>
        <Icon name={ICONOS[item.tone]} />
      </span>
      <span className={styles.body}>
        <p className={styles.title}>{item.title}</p>
        {item.message === undefined ? null : <p className={styles.message}>{item.message}</p>}
      </span>
      <button
        aria-label="Cerrar aviso"
        className={styles.close}
        onClick={() => {
          onDismiss(item.id);
        }}
        type="button"
      >
        ×
      </button>
    </div>
  );
}
